import React, { useEffect, useRef, useState } from 'react';
import {
  Plus, Trash2, ArrowUp, ArrowDown, CheckCircle2, CheckSquare, Circle, Download, ClipboardCopy,
} from 'lucide-react';
import { FilePicker, InfoTip, inputCls } from '../shared/FormKit';
import { btnChooseFile } from '../workflow/constants';
import { QUESTION_TYPE_LABELS } from './quizFormat';
// The Excel round trip: a template that tells a conversational AI assistant
// how to write a paper, and the reader for the file it gives back.
import { downloadQuizTemplate, parseQuizWorkbook, QUIZ_PROMPT } from './quizWorkbook';

/**
 * The questions of a quiz paper, as cards — shared by the Quiz Bank editor
 * (pages/LDQuizEditor.js) and the trainer's session-page editor
 * (pages/MySessionQuizEditor.js), so a paper is written the same way wherever
 * it is written (docs/modules/ld.md §5).
 *
 * Controlled: the caller owns `questions` (editor shape, see blankQuestion)
 * and passes the state setter; this component only knows how to change the
 * list. `locked` greys everything out — the paper has been sat.
 *
 * The model helpers travel with the component so both editors validate and
 * serialise identically: blankQuestion / fromServer / questionProblems /
 * toPayload.
 *
 * Above the cards sits the Excel round trip (quizWorkbook.js): download a
 * template that tells a conversational AI assistant how to write the paper,
 * import the filled-in file as drafts. It rides with the cards, so both
 * editors get it from the one place.
 */

export const MAX_OPTIONS = 10;
const OPTION_LETTERS = 'ABCDEFGHIJ';

const blankOption = () => ({ text: '', correct: false });

export const blankQuestion = (type = 'single') => ({
  uid: Math.random().toString(36).slice(2),
  type,
  text: '',
  points: 1,
  mustPass: false,
  options: [blankOption(), blankOption(), blankOption(), blankOption()],
});

// Server → editor: options carry {key, text} + a separate correct[] list;
// the editor works with a boolean per option.
export const fromServer = (q) => ({
  uid: String(q.id),
  type: q.type,
  text: q.text,
  points: q.points,
  mustPass: q.mustPass === true,
  options: (q.options || []).map((o) => ({ text: o.text, correct: (q.correct || []).includes(o.key) })),
});

// Editor → request body (services/quizPaper.js re-keys the options).
export const toPayload = (questions) => questions.map((q) => ({
  type: q.type,
  text: q.text.trim(),
  points: Number(q.points) || 1,
  mustPass: !!q.mustPass,
  options: q.options.map((o) => ({ text: o.text.trim(), correct: !!o.correct })),
}));

// The rules the server applies, checked here so the banner can name the
// question rather than bouncing a 400 back. `label` is how the question is
// pointed at: 'Question 3' on the page, 'Row 7' on an Excel import.
export const questionProblems = (q, label) => {
  if (!q.text.trim()) return `${label}: the question text is missing.`;
  const filled = q.options.filter((o) => o.text.trim());
  if (filled.length < 2) return `${label}: at least two options are needed.`;
  if (q.options.some((o) => !o.text.trim())) return `${label}: an option is empty — fill it in or remove it.`;
  const correct = q.options.filter((o) => o.correct).length;
  if (q.type === 'single' && correct !== 1) return `${label}: a single-choice question needs exactly one correct option.`;
  if (q.type === 'multiple' && correct < 1) return `${label}: tick at least one correct option.`;
  const pts = Number(q.points);
  if (!Number.isInteger(pts) || pts < 1 || pts > 100) return `${label}: points must be a whole number from 1 to 100.`;
  return null;
};

// First problem across the paper, or null when it is ready to save / preview.
export const paperProblem = (questions) => {
  for (let i = 0; i < questions.length; i += 1) {
    const problem = questionProblems(questions[i], `Question ${i + 1}`);
    if (problem) return problem;
  }
  return null;
};

// A card nobody has typed into — dropped when an import brings questions in,
// so a fresh editor ends up with exactly what the file held.
const isEmptyQuestion = (q) => !q.text.trim() && !q.options.some((o) => o.text.trim());

/**
 * Write the paper in Excel: download the template, hand it to a conversational
 * AI assistant with the prompt the file carries, import what comes back. The
 * questions land in the cards below as drafts — the import validates every
 * row with the rules above and refuses the file rather than loading half a
 * paper, and nothing reaches the database until Save.
 */
const QuizExcelTools = ({ setQuestions }) => {
  const copiedTimer = useRef(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [result, setResult] = useState(null);   // { ok, text, details[] }

  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  // The prompt goes to the assistant alongside the file. navigator.clipboard
  // needs a secure context, so an older or plain-http browser falls back to
  // the textarea trick rather than losing the button.
  const copyPrompt = async () => {
    setResult(null);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(QUIZ_PROMPT);
      } else {
        const box = document.createElement('textarea');
        box.value = QUIZ_PROMPT;
        box.setAttribute('readonly', '');
        box.style.position = 'fixed';
        box.style.opacity = '0';
        document.body.appendChild(box);
        box.select();
        const done = document.execCommand('copy');
        document.body.removeChild(box);
        if (!done) throw new Error('copy refused');
      }
      setCopied(true);
      clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      setResult({
        ok: false,
        text: 'The browser would not let the page copy. Open the template’s Instructions sheet and copy the prompt from there.',
        details: [],
      });
    }
  };

  const pick = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';                         // same file can be re-picked
    if (!file) return;
    setResult(null);
    setBusy(true);
    try {
      const { questions, rowNumbers, skipped, problems } = await parseQuizWorkbook(file);
      const all = [...problems];
      questions.forEach((q, i) => {
        const problem = questionProblems(q, `Row ${rowNumbers[i]}`);
        if (problem) all.push(problem);
      });
      if (all.length > 0) {
        setResult({
          ok: false,
          text: `Nothing imported — ${all.length} row${all.length === 1 ? '' : 's'} in ${file.name} need${all.length === 1 ? 's' : ''} fixing:`,
          details: all.slice(0, 8),
        });
        return;
      }
      setQuestions((list) => [...list.filter((q) => !isEmptyQuestion(q)), ...questions]);
      setResult({
        ok: true,
        text: `Added ${questions.length} question${questions.length === 1 ? '' : 's'} from ${file.name}`
          + `${skipped > 0 ? ` (${skipped} example row${skipped === 1 ? '' : 's'} skipped)` : ''}.`
          + ' Check every question and answer below, then press Save.',
        details: [],
      });
    } catch (err) {
      setResult({ ok: false, text: err.message, details: [] });
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <p className="text-sm font-semibold text-slate-900">Write the Questions in Excel</p>
      <p className="mt-1 text-xs text-slate-600 max-w-3xl">
        Download the template and copy the prompt, then give both to a conversational AI assistant along
        with the course material and import the file it hands back. The questions arrive as drafts in the
        cards below; read them through before saving, and keep client information out of a public AI
        assistant.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={downloadQuizTemplate} className={btnChooseFile}>
          <Download className="w-4 h-4" /> Download Excel Template
        </button>
        <button
          type="button"
          onClick={copyPrompt}
          title="Copy the prompt from the template's Instructions sheet, ready to paste into the assistant"
          className={btnChooseFile}
        >
          <ClipboardCopy className="w-4 h-4" /> {copied ? 'Prompt Copied' : 'Copy Prompt'}
        </button>
        <FilePicker kind="spreadsheet" showSelection={false} disabled={busy}
          buttonLabel={busy ? 'Reading…' : 'Import from Excel'} onChange={pick} />
      </div>
      {result && (
        <div
          className={`mt-3 p-3 rounded-lg border text-sm ${
            result.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'}`}
        >
          <p>{result.text}</p>
          {result.details.length > 0 && (
            <ul className="mt-1.5 space-y-0.5 text-xs list-disc list-inside">
              {result.details.map((d, i) => <li key={i}>{d}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

const QuizQuestionsEditor = ({ questions, setQuestions, locked = false }) => {
  const patchQuestion = (uid, patch) => setQuestions((list) => list.map((q) => (q.uid === uid ? { ...q, ...patch } : q)));
  const setType = (uid, type) => setQuestions((list) => list.map((q) => {
    if (q.uid !== uid) return q;
    // Going multiple → single keeps only the first tick so the rule holds.
    let options = q.options;
    if (type === 'single') {
      let seen = false;
      options = q.options.map((o) => {
        if (o.correct && !seen) { seen = true; return o; }
        return { ...o, correct: false };
      });
    }
    return { ...q, type, options };
  }));
  const patchOption = (uid, idx, patch) => setQuestions((list) => list.map((q) => {
    if (q.uid !== uid) return q;
    const options = q.options.map((o, i) => (i === idx ? { ...o, ...patch } : o));
    return { ...q, options };
  }));
  const markCorrect = (uid, idx) => setQuestions((list) => list.map((q) => {
    if (q.uid !== uid) return q;
    const options = q.options.map((o, i) => (q.type === 'single'
      ? { ...o, correct: i === idx }
      : (i === idx ? { ...o, correct: !o.correct } : o)));
    return { ...q, options };
  }));
  const addOption = (uid) => setQuestions((list) => list.map((q) => (
    q.uid === uid && q.options.length < MAX_OPTIONS ? { ...q, options: [...q.options, blankOption()] } : q)));
  const removeOption = (uid, idx) => setQuestions((list) => list.map((q) => (
    q.uid === uid && q.options.length > 2 ? { ...q, options: q.options.filter((_, i) => i !== idx) } : q)));
  const move = (uid, dir) => setQuestions((list) => {
    const i = list.findIndex((q) => q.uid === uid);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= list.length) return list;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const removeQuestion = (uid) => setQuestions((list) => (list.length > 1 ? list.filter((q) => q.uid !== uid) : list));
  const addQuestion = (type) => setQuestions((list) => [...list, blankQuestion(type)]);

  return (
    <>
      {!locked && <QuizExcelTools setQuestions={setQuestions} />}

      {questions.map((q, qi) => {
        const n = qi + 1;
        const correctCount = q.options.filter((o) => o.correct).length;
        return (
          <div key={q.uid} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="flex flex-wrap items-center gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200">
              <span className="text-sm font-semibold text-slate-900">Question {n}</span>
              {/* Type: two pills, not a select — the choice changes how the
                  option column behaves, so it should be visible at a glance. */}
              <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden text-xs font-medium">
                {['single', 'multiple'].map((t) => (
                  <button
                    key={t}
                    type="button"
                    disabled={locked}
                    onClick={() => setType(q.uid, t)}
                    className={`px-2.5 py-1.5 transition-colors disabled:opacity-60 ${
                      q.type === t ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
                  >
                    {QUESTION_TYPE_LABELS[t]}
                  </button>
                ))}
              </div>
              <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                Points
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={q.points}
                  disabled={locked}
                  onChange={(e) => patchQuestion(q.uid, { points: e.target.value })}
                  className="w-16 px-2 py-1 border border-slate-300 rounded-lg text-sm disabled:bg-slate-100"
                />
              </label>
              {/* Critical question — part of the paper, so it freezes with it. */}
              <label
                className={`inline-flex items-center gap-1.5 text-xs cursor-pointer ${q.mustPass ? 'text-amber-800 font-medium' : 'text-slate-600'}`}
              >
                <input
                  type="checkbox"
                  checked={!!q.mustPass}
                  disabled={locked}
                  onChange={(e) => patchQuestion(q.uid, { mustPass: e.target.checked })}
                  className="h-3.5 w-3.5 rounded text-amber-600 focus:ring-amber-500 border-slate-300"
                />
                Must answer correctly
                <InfoTip text="Critical question: whoever gets it wrong is marked Not passed, even if their overall score reaches the pass mark. With no pass mark set, passing = answering every critical question correctly." />
              </label>
              <div className="ml-auto inline-flex items-center gap-1">
                <button type="button" disabled={locked || qi === 0} onClick={() => move(q.uid, -1)} className="p-1.5 rounded text-slate-500 hover:bg-slate-200 disabled:opacity-30" title="Move up">
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button type="button" disabled={locked || qi === questions.length - 1} onClick={() => move(q.uid, 1)} className="p-1.5 rounded text-slate-500 hover:bg-slate-200 disabled:opacity-30" title="Move down">
                  <ArrowDown className="w-4 h-4" />
                </button>
                <button type="button" disabled={locked || questions.length === 1} onClick={() => removeQuestion(q.uid)} className="p-1.5 rounded text-slate-500 hover:text-red-600 hover:bg-red-50 disabled:opacity-30" title="Remove question">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="p-4 space-y-3">
              <textarea
                rows="2"
                value={q.text}
                disabled={locked}
                onChange={(e) => patchQuestion(q.uid, { text: e.target.value })}
                className={`${inputCls} disabled:bg-slate-50`}
                placeholder="Type the question…"
                maxLength={2000}
              />
              <div>
                <p className="text-xs text-slate-500 mb-1.5">
                  {q.type === 'single'
                    ? 'Options — mark the one correct answer.'
                    : 'Options — tick every correct answer (all must be picked to score).'}
                  {correctCount > 0 && <span className="ml-1 text-slate-400">({correctCount} marked correct)</span>}
                </p>
                <div className="space-y-1.5">
                  {q.options.map((o, oi) => {
                    const Mark = q.type === 'single' ? Circle : CheckSquare;
                    return (
                      <div key={oi} className="flex items-center gap-2">
                        <button
                          type="button"
                          disabled={locked}
                          onClick={() => markCorrect(q.uid, oi)}
                          title={o.correct ? 'Correct answer' : 'Mark as correct'}
                          className={`shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg border transition-colors disabled:opacity-60 ${
                            o.correct
                              ? 'bg-emerald-600 border-emerald-600 text-white'
                              : 'bg-white border-slate-300 text-slate-400 hover:border-emerald-400'}`}
                        >
                          {o.correct ? <CheckCircle2 className="w-4 h-4" /> : <Mark className="w-4 h-4" />}
                        </button>
                        <span className="w-5 text-xs font-semibold text-slate-500 text-center">{OPTION_LETTERS[oi]}</span>
                        <input
                          type="text"
                          value={o.text}
                          disabled={locked}
                          onChange={(e) => patchOption(q.uid, oi, { text: e.target.value })}
                          className={`${inputCls} disabled:bg-slate-50`}
                          placeholder={`Option ${OPTION_LETTERS[oi]}`}
                          maxLength={2000}
                        />
                        <button
                          type="button"
                          disabled={locked || q.options.length <= 2}
                          onClick={() => removeOption(q.uid, oi)}
                          className="shrink-0 p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30"
                          title="Remove option"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
                {!locked && q.options.length < MAX_OPTIONS && (
                  <button type="button" onClick={() => addOption(q.uid)} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800">
                    <Plus className="w-3.5 h-3.5" /> Add Option
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {!locked && (
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => addQuestion('single')} className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-dashed border-blue-300 text-blue-700 text-sm font-medium rounded-lg hover:bg-blue-50">
            <Plus className="w-4 h-4" /> Add Single-Choice Question
          </button>
          <button type="button" onClick={() => addQuestion('multiple')} className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-dashed border-blue-300 text-blue-700 text-sm font-medium rounded-lg hover:bg-blue-50">
            <Plus className="w-4 h-4" /> Add Multiple-Choice Question
          </button>
        </div>
      )}
    </>
  );
};

export default QuizQuestionsEditor;
